import { Box, TableCell } from "@mui/material";
import { FormFieldCellEditor } from "components/FormField/FormFieldCellEditor";
import { CaseInfo, Cell, FieldCellValue } from "generated/graphql";
import { StateHandle } from "StateHandle";
import { grey } from "theme/palette";
import { P } from "theme/typography";
import { cellValueFromGQL, gqlCellValueFromValue } from "Backend";
import produce from "immer";
import { validateCell } from "helpers/FormHelpers";

interface HitTableEditRowProps {
  caseInfo?: CaseInfo;
  cells: Cell[];
  stateHandle: StateHandle<FieldCellValue[]>;
  disabled?: boolean;
  colSpan?: number;
  selectHit: JSX.Element;
  hitActions?: JSX.Element;
}

const HitTableEditRow = ({
  caseInfo,
  cells,
  selectHit,
  stateHandle,
  disabled,
  colSpan,
  hitActions,
}: HitTableEditRowProps) => {
  const [state, setState] = stateHandle;

  if (state.length < cells.length) {
    return <P>Loading</P>;
  }

  const makeCell = (cell: Cell, cellIndex: number, isValid: boolean, showLabel?: boolean) => {
    return (
      <FormFieldCellEditor
        isValid={isValid}
        variant="dense"
        label={showLabel ? cell.name : ""}
        caseInfo={caseInfo}
        cell={cell}
        tabular={false}
        disabled={disabled}
        key={cell.id}
        value={cellValueFromGQL(state[cellIndex]?.value)}
        setValue={(v) => {
          setState(
            produce((draft) => {
              const cellValue = draft[cellIndex];

              if (cellValue !== undefined) {
                cellValue.value = gqlCellValueFromValue(cell.type, v);
              }
            })
          );
        }}
      />
    );
  };

  return colSpan === undefined || colSpan === cells.length ? (
    <>
      {cells.map((cell, cellIndex) => {
        const cellIsValid = validateCell(state[cellIndex], cell);

        return <TableCell key={cell.id}>{makeCell(cell, cellIndex, cellIsValid)}</TableCell>;
      })}
    </>
  ) : (
    <TableCell
      colSpan={colSpan + 1}
      sx={{
        border: `1px solid ${grey[400]}`,
      }}>
      <Box sx={{ display: "flex" }}>
        <Box sx={{ width: "36px" }}>{selectHit}</Box>
        <Box
          sx={{
            flexGrow: 1,
            display: "grid",
            gridTemplateColumns: `repeat(auto-fill, minmax(133px, 1fr))`,
            gap: "8px 8px",
            paddingY: 1,
          }}>
          {cells.map((cell, cellIndex) => {
            const cellIsValid = validateCell(state[cellIndex], cell);
            return makeCell(cell, cellIndex, cellIsValid, true);
          })}
        </Box>
        {hitActions && <Box sx={{ width: "36px" }}>{hitActions}</Box>}
      </Box>
    </TableCell>
  );
};

export default HitTableEditRow;
